import {expandEntityIdsByChartDistance} from './expandEntityIdsByChartDistance';

/**
 * @typedef {'entityType'|'property'} EntityFilterKind
 */

/**
 * @typedef {Object} EntityFilterRow
 * @property {string} id
 * @property {EntityFilterKind} [kind]
 * @property {string} [propertyKey]
 * @property {string} [operator]
 * @property {string[]} [values]
 * @property {string} [value] legacy single-value field
 */

function normalizeComparableString(raw) {
    if (raw == null) return '';
    return String(raw).trim().toLowerCase();
}

function rowValues(row) {
    if (Array.isArray(row?.values)) {
        return row.values
            .map((value) => (value == null ? '' : String(value).trim()))
            .filter(Boolean);
    }
    if (row?.value == null) return [];
    const legacy = String(row.value).trim();
    return legacy ? [legacy] : [];
}

/**
 * @param {Array<EntityFilterRow>|null|undefined} filters
 * @returns {Array<{ kind: 'entityType', values: string[] }|{ kind: 'property', propertyKey: string, operator: string, values: string[] }>}
 */
export function getCompleteFilters(filters) {
    if (!Array.isArray(filters)) return [];

    const complete = [];

    for (const row of filters) {
        const values = rowValues(row);
        if (!values.length) continue;

        if (row?.kind === 'entityType') {
            complete.push({kind: 'entityType', values});
            continue;
        }

        const propertyKey = typeof row?.propertyKey === 'string' ? row.propertyKey.trim() : '';
        if (!propertyKey) continue;

        complete.push({
            kind: 'property',
            propertyKey,
            operator: row.operator || '=',
            values,
        });
    }

    return complete;
}

/**
 * @param {Array<EntityFilterRow>|null|undefined} filters
 * @returns {boolean}
 */
export function isEntityFilterActive(filters) {
    return getCompleteFilters(filters).length > 0;
}

/**
 * Parse a leading numeric token (e.g. "16.0" from "16.0, 64.0").
 * @param {unknown} raw
 * @returns {number|null}
 */
export function parseLeadingNumber(raw) {
    if (raw == null) return null;
    const text = String(raw).trim();
    if (!text) return null;
    const match = text.match(/^[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/);
    if (!match) return null;
    const num = Number(match[0]);
    return Number.isFinite(num) ? num : null;
}

function itemMatchesPropertyKey(item, propertyKey) {
    if (typeof item?.propertyId === 'string' && item.propertyId.trim()) {
        return item.propertyId.trim() === propertyKey;
    }
    const label = typeof item?.propertyLabel === 'string' ? item.propertyLabel.trim() : '';
    return Boolean(label) && label === propertyKey;
}

function equalsMatch(item, filterValue) {
    const needle = normalizeComparableString(filterValue);
    if (!needle) return false;
    const candidates = [item?.value, item?.valueEntityId, item?.valueLabel];
    return candidates.some((candidate) => normalizeComparableString(candidate) === needle);
}

function itemSatisfiesOperator(item, operator, filterValue) {
    if (operator === '=') {
        return equalsMatch(item, filterValue);
    }
    if (operator === '<>') {
        return !equalsMatch(item, filterValue);
    }
    if (operator === '<' || operator === '>') {
        const left = parseLeadingNumber(item?.value);
        const right = parseLeadingNumber(filterValue);
        if (left == null || right == null) return false;
        return operator === '<' ? left < right : left > right;
    }
    return false;
}

/**
 * @param {Array<object>} propertyItems
 * @param {{ propertyKey: string, operator?: string, value?: string, values?: string[] }} filter
 * @returns {boolean}
 */
export function propertyItemsPassFilter(propertyItems, filter) {
    const matchingItems = (propertyItems || []).filter((item) => (
        itemMatchesPropertyKey(item, filter.propertyKey)
    ));
    if (matchingItems.length === 0) return false;

    const values = filter.values?.length
        ? filter.values
        : (filter.value != null && String(filter.value).trim() ? [String(filter.value).trim()] : []);
    if (!values.length) return false;

    return values.some((filterValue) => (
        matchingItems.some((item) => itemSatisfiesOperator(item, filter.operator || '=', filterValue))
    ));
}

/**
 * @param {string|null|undefined} entityType
 * @param {string[]} filterValues
 * @returns {boolean}
 */
export function entityTypePassesFilter(entityType, filterValues) {
    if (!filterValues.length) return true;
    if (!entityType) return false;
    const normalized = normalizeComparableString(entityType);
    return filterValues.some((value) => normalizeComparableString(value) === normalized);
}

/**
 * @param {string} entityId
 * @param {Array<object>} propertyItems
 * @param {string|null|undefined} entityType
 * @param {ReturnType<typeof getCompleteFilters>} completeFilters
 * @returns {boolean}
 */
export function entityPassesFilters(entityId, propertyItems, entityType, completeFilters) {
    if (!completeFilters.length) return true;

    return completeFilters.every((filter) => {
        if (filter.kind === 'entityType') {
            return entityTypePassesFilter(entityType, filter.values);
        }
        return propertyItemsPassFilter(propertyItems, filter);
    });
}

/**
 * Entity-type filters need summary metadata; skip them until summaries finish loading.
 *
 * @param {ReturnType<typeof getCompleteFilters>} completeFilters
 * @param {boolean} [summariesLoading]
 * @returns {ReturnType<typeof getCompleteFilters>}
 */
export function getAppliedCompleteFilters(completeFilters, summariesLoading = false) {
    if (!summariesLoading) return completeFilters;
    return completeFilters.filter((filter) => filter.kind !== 'entityType');
}

/**
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @param {Record<string, Array<object>>} propertiesByEntityId
 * @param {Record<string, { entityType?: string|null }>|null|undefined} summariesByEntityId
 * @param {Array<EntityFilterRow>|null|undefined} filters
 * @param {{ summariesLoading?: boolean }} [options]
 * @returns {Set<string>}
 */
export function getMatchingEntityIds(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    {summariesLoading = false} = {},
) {
    const completeFilters = getAppliedCompleteFilters(getCompleteFilters(filters), summariesLoading);
    const ids = new Set();

    for (const entity of chartEntities || []) {
        const entityId = entity?.wikidataId;
        if (!entityId) continue;
        const items = propertiesByEntityId?.[entityId] || [];
        const entityType = summariesByEntityId?.[entityId]?.entityType ?? null;
        if (entityPassesFilters(entityId, items, entityType, completeFilters)) {
            ids.add(entityId);
        }
    }

    return ids;
}

/**
 * Seed entities matching filters, expanded along chart connections up to maxDistance.
 *
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @param {Record<string, Array<object>>} propertiesByEntityId
 * @param {Record<string, { entityType?: string|null }>|null|undefined} summariesByEntityId
 * @param {Array<EntityFilterRow>|null|undefined} filters
 * @param {Array<{ from: string, to: string }>|null|undefined} chartConnections
 * @param {number} [maxDistance]
 * @param {{ summariesLoading?: boolean }} [options]
 * @returns {Set<string>}
 */
export function getVisibleEntityIds(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    chartConnections,
    maxDistance,
    {summariesLoading = false} = {},
) {
    const seedIds = getMatchingEntityIds(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        filters,
        {summariesLoading},
    );
    return expandEntityIdsByChartDistance(seedIds, chartConnections, maxDistance);
}

/**
 * Content-stable key for matching entity ids. Returns null when filters are inactive
 * so React consumers can keep referential stability (important for tldraw's
 * getShapeVisibility, which remounts the Editor when the callback identity changes).
 *
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @param {Record<string, Array<object>>} propertiesByEntityId
 * @param {Record<string, { entityType?: string|null }>|null|undefined} summariesByEntityId
 * @param {Array<EntityFilterRow>|null|undefined} filters
 * @param {Array<{ from: string, to: string }>|null|undefined} chartConnections
 * @param {number} [maxDistance]
 * @param {{ summariesLoading?: boolean }} [options]
 * @returns {string|null}
 */
export function getVisibleEntityIdsKey(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    chartConnections,
    maxDistance,
    {summariesLoading = false} = {},
) {
    const completeFilters = getAppliedCompleteFilters(getCompleteFilters(filters), summariesLoading);
    if (!completeFilters.length) return null;
    const ids = getVisibleEntityIds(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        filters,
        chartConnections,
        maxDistance,
        {summariesLoading},
    );
    return Array.from(ids).sort().join('\0');
}

/**
 * @param {string|null} visibleEntityIdsKey
 * @returns {Set<string>|null}
 */
export function visibleEntityIdsFromKey(visibleEntityIdsKey) {
    if (visibleEntityIdsKey === null) return null;
    if (!visibleEntityIdsKey) return new Set();
    return new Set(visibleEntityIdsKey.split('\0'));
}

export function createEntityTypeFilterRow() {
    return {
        id: 'entity-type',
        kind: 'entityType',
        values: [],
    };
}

export function createEmptyPropertyFilterRow() {
    return {
        id: `filter-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        kind: 'property',
        propertyKey: '',
        operator: '=',
        values: [],
    };
}

/** @deprecated use createEmptyPropertyFilterRow */
export function createEmptyFilterRow() {
    return createEmptyPropertyFilterRow();
}

export function createDefaultEntityFilters() {
    return [createEntityTypeFilterRow(), createEmptyPropertyFilterRow()];
}

/**
 * Drop property rows that just lost every value (chip deselect or stale prune).
 * Keeps a single blank property slot so the Entities panel still has a row.
 *
 * @param {Array<EntityFilterRow>|null|undefined} nextFilters
 * @param {Array<EntityFilterRow>|null|undefined} [previousFilters]
 * @returns {Array<EntityFilterRow>}
 */
export function compactEmptiedPropertyFilterRows(nextFilters, previousFilters = []) {
    const previousById = new Map((previousFilters || []).map((row) => [row?.id, row]));
    const compacted = [];
    for (const row of nextFilters || []) {
        if (!row) continue;
        if (row.kind === 'entityType') {
            compacted.push(row);
            continue;
        }
        const hadValues = rowValues(previousById.get(row.id)).length > 0;
        const hasValues = rowValues(row).length > 0;
        const hasKey = typeof row.propertyKey === 'string' && Boolean(row.propertyKey.trim());
        if (hadValues && !hasValues && hasKey) {
            continue;
        }
        compacted.push(row);
    }
    if (!compacted.some((row) => row.kind === 'entityType')) {
        compacted.unshift(createEntityTypeFilterRow());
    }
    if (!compacted.some((row) => row.kind !== 'entityType')) {
        compacted.push(createEmptyPropertyFilterRow());
    }
    return compacted;
}
